import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

export function VoiceInput({ onResult }) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'it-IT';

      recognitionRef.current.onresult = (event) => {
        const current = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setTranscript(current);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        toast.error("Errore nel riconoscimento vocale");
      };

      recognitionRef.current.onend = () => {
        if (isListening) {
          // Auto restart if still listening
          try {
            recognitionRef.current.start();
          } catch (e) {
            setIsListening(false);
          }
        }
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [isListening]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      toast.error("Il riconoscimento vocale non è supportato dal tuo browser");
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      if (transcript && onResult) {
        onResult(transcript);
      }
      setTranscript("");
    } else {
      setTranscript("");
      recognitionRef.current.start();
      setIsListening(true);
      toast.info("Parla ora... Clicca di nuovo per terminare");
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant={isListening ? "destructive" : "outline"}
        size="icon"
        onClick={toggleListening}
        className={isListening ? "recording-pulse" : ""}
        data-testid="voice-record-btn"
       aria-label="Dettatura vocale">
        {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </Button>
      {isListening && transcript && (
        <span className="text-sm text-muted-foreground max-w-xs truncate">
          {transcript}
        </span>
      )}
    </div>
  );
}

export default VoiceInput;
